/**
 * HRPayrollTab — Onglet "Paie & Charges" du module RH
 * Sous-onglet A : Fiches de paie Silae (upload, liste, marquer vu)
 * Sous-onglet B : Déclarations URSSAF (upload, montants éditables, total)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, Upload, Eye, Trash2, Building, CheckCircle,
  AlertCircle, Plus, X, Loader, Bell,
} from 'lucide-react';
import {
  getSilaeDocuments, uploadSilaeDocument, deleteSilaeDocument, markSilaeViewed,
  getDeclarations, uploadDeclaration, updateDeclarationAmount, deleteDeclaration,
} from '../../api';
import { useStructure } from '../../contexts/StructureContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

const YEARS = [2024, 2025, 2026, 2027];

const formatMonth = (month, year) => {
  const m = MONTHS.find(m => m.value === Number(month));
  return m ? `${m.label} ${year}` : `${month}/${year}`;
};

const fmtCurrency = (v) =>
  v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
};

const now = new Date();
const DEFAULT_MONTH = now.getMonth() + 1;
const DEFAULT_YEAR = now.getFullYear();

// ─── Utilitaires UI ───────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-500';
const selectCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-500 bg-white';

const Btn = ({ onClick, disabled, loading, variant = 'primary', size = 'md', children, className = '', type = 'button' }) => {
  const variants = {
    primary: 'bg-indigo-600 text-white hover:bg-indigo-700',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    danger: 'bg-red-600 text-white hover:bg-red-700',
    ghost: 'text-gray-600 hover:bg-gray-100',
  };
  const sizes = { sm: 'px-2.5 py-1.5 text-xs', md: 'px-3.5 py-2 text-sm', lg: 'px-5 py-2.5 text-base' };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && <Loader size={13} className="animate-spin" />}
      {children}
    </button>
  );
};

const SourceBadge = ({ source }) => {
  if (source === 'n8n' || source === 'email') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
        <Bell size={10} /> N8N
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
      Manuel
    </span>
  );
};

const NewBadge = () => (
  <span className="inline-flex items-center text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
    Nouveau
  </span>
);

// ─── Sélecteurs mois / année partagés ─────────────────────────────────────────

const MonthYearSelectors = ({ month, year, onMonthChange, onYearChange }) => (
  <div className="flex items-center gap-2">
    <select value={month} onChange={e => onMonthChange(Number(e.target.value))} className={selectCls}>
      {MONTHS.map(m => (
        <option key={m.value} value={m.value}>{m.label}</option>
      ))}
    </select>
    <select value={year} onChange={e => onYearChange(Number(e.target.value))} className={selectCls}>
      {YEARS.map(y => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  </div>
);

// ─── Section Fiches Silae ──────────────────────────────────────────────────────

const SilaeSection = ({ structures, currentStructure, month, year }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStructureId, setUploadStructureId] = useState(currentStructure?.id || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { month, year };
      const res = await getSilaeDocuments(params);
      setDocuments(res.data || []);
    } catch (e) {
      setError('Erreur lors du chargement des fiches de paie.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (currentStructure?.id) setUploadStructureId(currentStructure.id);
  }, [currentStructure]);

  const handleView = async (doc) => {
    window.open(doc.fileUrl, '_blank');
    if (!doc.viewedAt) {
      try {
        await markSilaeViewed(doc.id);
        setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, viewedAt: new Date().toISOString() } : d));
      } catch {}
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette fiche de paie ?')) return;
    try {
      await deleteSilaeDocument(id);
      fetchDocuments();
    } catch {
      alert('Erreur lors de la suppression.');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadStructureId) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('structureId', uploadStructureId);
    formData.append('month', month);
    formData.append('year', year);
    try {
      await uploadSilaeDocument(formData);
      setShowUpload(false);
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments();
    } catch {
      alert('Erreur lors de l\'upload. Vérifiez le fichier (PDF, max 5 Mo).');
    } finally {
      setUploading(false);
    }
  };

  // Regroupement par structure
  const structureMap = {};
  structures.forEach(s => { structureMap[s.id] = s.name; });

  // Filtrage employé
  const employees = [...new Set(documents.map(d => d.employeeName).filter(Boolean))].sort();
  const filtered = selectedEmployee === 'all'
    ? documents
    : documents.filter(d => d.employeeName === selectedEmployee);

  // Regroupement par structure
  const grouped = {};
  filtered.forEach(doc => {
    const sName = structureMap[doc.structureId] || doc.structureId || 'Structure inconnue';
    if (!grouped[sName]) grouped[sName] = [];
    grouped[sName].push(doc);
  });

  const unviewedCount = documents.filter(d => !d.viewedAt).length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select
            value={selectedEmployee}
            onChange={e => setSelectedEmployee(e.target.value)}
            className={selectCls}
          >
            <option value="all">Tous les employés</option>
            {employees.map(emp => (
              <option key={emp} value={emp}>{emp}</option>
            ))}
          </select>
          {unviewedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
              <AlertCircle size={12} />
              {unviewedCount} nouvelle{unviewedCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <Btn onClick={() => setShowUpload(v => !v)}>
          <Upload size={15} />
          Upload fiche de paie
        </Btn>
      </div>

      {/* Formulaire upload */}
      {showUpload && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-indigo-800">Ajouter une fiche de paie</p>
            <button onClick={() => { setShowUpload(false); setUploadFile(null); }} className="text-indigo-400 hover:text-indigo-600">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Structure <span className="text-red-500">*</span></label>
              <select
                value={uploadStructureId}
                onChange={e => setUploadStructureId(e.target.value)}
                className={selectCls + ' w-full'}
              >
                <option value="">-- Choisir --</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fichier PDF <span className="text-red-500">*</span></label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={e => setUploadFile(e.target.files[0] || null)}
                className="w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Btn onClick={handleUpload} loading={uploading} disabled={!uploadFile || !uploadStructureId}>
              <CheckCircle size={14} /> Enregistrer
            </Btn>
            <Btn variant="secondary" onClick={() => { setShowUpload(false); setUploadFile(null); }}>
              Annuler
            </Btn>
          </div>
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader className="animate-spin text-indigo-500" size={24} />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-red-600 text-sm p-4 bg-red-50 rounded-xl border border-red-200">
          <AlertCircle size={16} /> {error}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          <FileText size={32} className="mx-auto mb-3 opacity-30" />
          Aucune fiche de paie pour {formatMonth(month, year)}.
        </div>
      ) : (
        Object.entries(grouped).map(([structureName, docs]) => (
          <div key={structureName} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* En-tête structure */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 border-b border-indigo-100">
              <Building size={15} className="text-indigo-500" />
              <span className="text-sm font-semibold text-indigo-800">{structureName}</span>
              <span className="ml-auto text-xs text-indigo-500">{docs.length} fiche{docs.length > 1 ? 's' : ''}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Employé', 'Période', 'Source', 'Statut', ''].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {docs.map(doc => (
                  <tr key={doc.id} className={`transition-colors ${!doc.viewedAt ? 'bg-red-50 hover:bg-red-50/80' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-800">{doc.employeeName || doc.fileName || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatMonth(doc.month, doc.year)}</td>
                    <td className="px-4 py-3">
                      <SourceBadge source={doc.source} />
                    </td>
                    <td className="px-4 py-3">
                      {!doc.viewedAt ? (
                        <NewBadge />
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">
                          <CheckCircle size={11} /> Vu le {fmtDate(doc.viewedAt)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(doc)}
                          title="Voir le PDF"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors"
                        >
                          <Eye size={13} /> Voir PDF
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          title="Supprimer"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Section Déclarations URSSAF ──────────────────────────────────────────────

const DeclarationsSection = ({ structures, currentStructure, month, year }) => {
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    structureId: currentStructure?.id || '',
    amount: '',
  });
  const [uploading, setUploading] = useState(false);
  const [editingAmount, setEditingAmount] = useState({}); // { [id]: string }
  const [savingAmount, setSavingAmount] = useState({}); // { [id]: bool }
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const fetchDeclarations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDeclarations({ month, year });
      setDeclarations(res.data || []);
    } catch {
      setError('Erreur lors du chargement des déclarations.');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchDeclarations();
  }, [fetchDeclarations]);

  useEffect(() => {
    if (currentStructure?.id) {
      setUploadForm(prev => ({ ...prev, structureId: currentStructure.id }));
    }
  }, [currentStructure]);

  const structureMap = {};
  structures.forEach(s => { structureMap[s.id] = s.name; });

  // Regroupement par structure
  const grouped = {};
  declarations.forEach(decl => {
    const sName = structureMap[decl.structureId] || decl.structureId || 'Structure inconnue';
    if (!grouped[sName]) grouped[sName] = [];
    grouped[sName].push(decl);
  });

  // Total du mois : somme si toutes les structures ont une déclaration
  const totalAmount = structures.length > 0 && declarations.length >= structures.length
    ? declarations.reduce((sum, d) => sum + (Number(d.amount) || 0), 0)
    : null;

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.structureId) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('structureId', uploadForm.structureId);
    formData.append('month', month);
    formData.append('year', year);
    if (uploadForm.amount) formData.append('amount', uploadForm.amount);
    try {
      await uploadDeclaration(formData);
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ structureId: currentStructure?.id || '', amount: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDeclarations();
    } catch {
      alert('Erreur lors de l\'upload. Vérifiez le fichier (PDF, max 5 Mo).');
    } finally {
      setUploading(false);
    }
  };

  const handleAmountEdit = (id, currentAmount) => {
    setEditingAmount(prev => ({ ...prev, [id]: String(currentAmount ?? '') }));
  };

  const handleAmountSave = async (id) => {
    const value = editingAmount[id];
    if (value === undefined) return;
    const parsed = parseFloat(value.replace(',', '.'));
    if (isNaN(parsed)) return;
    setSavingAmount(prev => ({ ...prev, [id]: true }));
    try {
      await updateDeclarationAmount(id, parsed);
      setDeclarations(prev => prev.map(d => d.id === id ? { ...d, amount: parsed } : d));
      setEditingAmount(prev => { const next = { ...prev }; delete next[id]; return next; });
    } catch {
      alert('Erreur lors de la mise à jour du montant.');
    } finally {
      setSavingAmount(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleAmountCancel = (id) => {
    setEditingAmount(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette déclaration URSSAF ?')) return;
    try {
      await deleteDeclaration(id);
      fetchDeclarations();
    } catch {
      alert('Erreur lors de la suppression.');
    }
  };

  const handleView = (decl) => {
    window.open(decl.fileUrl, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Déclarations URSSAF mensuelles par structure — {formatMonth(month, year)}.
        </p>
        <Btn onClick={() => setShowUpload(v => !v)}>
          <Upload size={15} />
          Upload déclaration
        </Btn>
      </div>

      {/* Formulaire upload */}
      {showUpload && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-indigo-800">Ajouter une déclaration URSSAF</p>
            <button onClick={() => { setShowUpload(false); setUploadFile(null); }} className="text-indigo-400 hover:text-indigo-600">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Structure <span className="text-red-500">*</span></label>
              <select
                value={uploadForm.structureId}
                onChange={e => setUploadForm(prev => ({ ...prev, structureId: e.target.value }))}
                className={selectCls + ' w-full'}
              >
                <option value="">-- Choisir --</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Montant (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="ex : 1250.50"
                value={uploadForm.amount}
                onChange={e => setUploadForm(prev => ({ ...prev, amount: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Fichier PDF <span className="text-red-500">*</span></label>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                onChange={e => setUploadFile(e.target.files[0] || null)}
                className="w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Btn onClick={handleUpload} loading={uploading} disabled={!uploadFile || !uploadForm.structureId}>
              <CheckCircle size={14} /> Enregistrer
            </Btn>
            <Btn variant="secondary" onClick={() => { setShowUpload(false); setUploadFile(null); }}>
              Annuler
            </Btn>
          </div>
        </div>
      )}

      {/* Total du mois si complet */}
      {totalAmount !== null && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">
              Total charges URSSAF — {formatMonth(month, year)}
            </p>
            <p className="text-lg font-bold text-green-700">{fmtCurrency(totalAmount)}</p>
          </div>
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center py-10">
          <Loader className="animate-spin text-indigo-500" size={24} />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 text-red-600 text-sm p-4 bg-red-50 rounded-xl border border-red-200">
          <AlertCircle size={16} /> {error}
        </div>
      ) : declarations.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          <FileText size={32} className="mx-auto mb-3 opacity-30" />
          Aucune déclaration URSSAF pour {formatMonth(month, year)}.
        </div>
      ) : (
        Object.entries(grouped).map(([structureName, decls]) => (
          <div key={structureName} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* En-tête structure */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 border-b border-indigo-100">
              <Building size={15} className="text-indigo-500" />
              <span className="text-sm font-semibold text-indigo-800">{structureName}</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Structure', 'Période', 'Montant', 'Source', ''].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {decls.map(decl => {
                  const isEditing = decl.id in editingAmount;
                  return (
                    <tr key={decl.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 font-medium">{structureName}</td>
                      <td className="px-4 py-3 text-gray-600">{formatMonth(decl.month, decl.year)}</td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingAmount[decl.id]}
                              onChange={e => setEditingAmount(prev => ({ ...prev, [decl.id]: e.target.value }))}
                              className="w-28 border border-indigo-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleAmountSave(decl.id);
                                if (e.key === 'Escape') handleAmountCancel(decl.id);
                              }}
                            />
                            <button
                              onClick={() => handleAmountSave(decl.id)}
                              disabled={savingAmount[decl.id]}
                              className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                              title="Valider"
                            >
                              {savingAmount[decl.id] ? <Loader size={13} className="animate-spin" /> : <CheckCircle size={14} />}
                            </button>
                            <button
                              onClick={() => handleAmountCancel(decl.id)}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                              title="Annuler"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAmountEdit(decl.id, decl.amount)}
                            className="font-semibold text-gray-800 hover:text-indigo-600 hover:underline transition-colors"
                            title="Cliquer pour modifier"
                          >
                            {decl.amount != null ? fmtCurrency(decl.amount) : <span className="text-gray-400 text-xs">Non renseigné</span>}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <SourceBadge source={decl.source} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {decl.fileUrl && (
                            <button
                              onClick={() => handleView(decl)}
                              title="Voir le PDF"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors"
                            >
                              <Eye size={13} /> Voir PDF
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(decl.id)}
                            title="Supprimer"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────────

const HRPayrollTab = () => {
  const { currentStructure, structures } = useStructure();
  const [activeSubTab, setActiveSubTab] = useState('silae');
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [year, setYear] = useState(DEFAULT_YEAR);

  // Compteur fiches non vues pour le badge du sous-onglet
  const [unviewedCount, setUnviewedCount] = useState(0);

  // On charge le compteur "nouvelles fiches" indépendamment pour le badge
  useEffect(() => {
    getSilaeDocuments({ month, year })
      .then(res => {
        const docs = res.data || [];
        setUnviewedCount(docs.filter(d => !d.viewedAt).length);
      })
      .catch(() => {});
  }, [month, year]);

  const subTabs = [
    {
      id: 'silae',
      label: 'Fiches de paie Silae',
      badge: unviewedCount > 0 ? unviewedCount : null,
    },
    {
      id: 'declarations',
      label: 'Déclarations URSSAF',
      badge: null,
    },
  ];

  return (
    <div className="space-y-4">
      {/* En-tête : sous-onglets + sélecteurs mois/année */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-0">
          {/* Sous-onglets */}
          <div className="flex gap-0.5">
            {subTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSubTab === tab.id
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Sélecteurs mois / année */}
          <div className="pb-3">
            <MonthYearSelectors
              month={month}
              year={year}
              onMonthChange={setMonth}
              onYearChange={setYear}
            />
          </div>
        </div>
        {/* Ligne séparatrice */}
        <div className="border-t border-gray-100" />
      </div>

      {/* Contenu du sous-onglet */}
      {activeSubTab === 'silae' && (
        <SilaeSection
          structures={structures}
          currentStructure={currentStructure}
          month={month}
          year={year}
        />
      )}

      {activeSubTab === 'declarations' && (
        <DeclarationsSection
          structures={structures}
          currentStructure={currentStructure}
          month={month}
          year={year}
        />
      )}
    </div>
  );
};

export default HRPayrollTab;
